import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';

interface ChangePasswordModalProps {
  onPasswordChanged?: () => void;
}

/**
 * パスワード変更モーダルコンポーネント
 * 既存ユーザーのパスワード変更機能を提供
 */
const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onPasswordChanged }) => {
  const { execute: apiCall, error: apiError } = useApi();
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // モーダルが開かれる時に状態をリセット
  useEffect(() => {
    const modal = document.getElementById('changePasswordModal');
    
    const handleModalShow = () => {
      // フォームデータをリセット
      setFormData({
        current_password: '',
        new_password: '',
        new_password_confirm: ''
      });
      // エラーをクリア
      setErrors({});
      // パスワード表示状態をリセット
      setShowPasswords({
        current: false,
        new: false,
        confirm: false
      });
      // ローディング状態をリセット
      setLoading(false);
    };

    const handleModalHide = () => {
      // モーダルが閉じられる時にも状態をリセット
      setFormData({
        current_password: '',
        new_password: '',
        new_password_confirm: ''
      });
      setErrors({});
      setShowPasswords({
        current: false,
        new: false,
        confirm: false
      });
      setLoading(false);
    };

    if (modal) {
      modal.addEventListener('show.bs.modal', handleModalShow);
      modal.addEventListener('hidden.bs.modal', handleModalHide);
      
      // クリーンアップ関数を必ず返す
      return () => {
        modal.removeEventListener('show.bs.modal', handleModalShow);
        modal.removeEventListener('hidden.bs.modal', handleModalHide);
      };
    }
    
    // modalが存在しない場合は空のクリーンアップ関数を返す
    return () => {};
  }, []);

  // フォーム入力の変更処理
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // エラーをクリア
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // パスワード変更処理
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    // バリデーション
    const newErrors: {[key: string]: string} = {};
    if (!formData.current_password) {
      newErrors.current_password = '現在のパスワードを入力してください';
    }
    if (!formData.new_password) {
      newErrors.new_password = '新しいパスワードを入力してください';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'パスワードは8文字以上で入力してください';
    }
    if (formData.new_password !== formData.new_password_confirm) {
      newErrors.new_password_confirm = 'パスワードが一致しません';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('認証が必要です');
      }

      const result = await apiCall('/api/auth/user/change-password/', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token.replace(/"/g, '')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: formData.current_password,
          new_password: formData.new_password,
        }),
      });

      // useApiフックのerror状態をチェック
      if (apiError) {
        throw new Error(apiError);
      }

      // 結果がnullの場合もエラー
      if (result === null) {
        throw new Error(apiError || 'パスワードの変更に失敗しました');
      }

      // パスワード変更成功時の処理
      setFormData({
        current_password: '',
        new_password: '',
        new_password_confirm: ''
      });
      onPasswordChanged?.();
      
      // モーダルを閉じる
      const modal = document.getElementById('changePasswordModal');
      const modalInstance = (window as typeof window & { bootstrap?: { Modal: { getInstance: (el: Element | null) => { hide: () => void } | null } } }).bootstrap?.Modal?.getInstance(modal);
      modalInstance?.hide();
      
      // 成功通知（編集モーダルと同じスタイル）
      const toast = document.createElement('div');
      toast.className = 'success-toast';
      toast.innerHTML = '✅ パスワードを変更しました';
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => document.body.removeChild(toast), 300);
      }, 3000);
    } catch (error: unknown) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Password change error:', error);
      }
      const errorMessage = error instanceof Error ? error.message : 'パスワードの変更に失敗しました';
      if (errorMessage.includes('正しくありません')) {
        setErrors({ current_password: '現在のパスワードが正しくありません' });
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  // パスワード表示切り替え
  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="modal fade" id="changePasswordModal" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content modern-auth-modal">
          
          {/* ヘッダー */}
          <div className="modern-auth-header">
            <div className="auth-logo">
              <img 
                src="/img/splice_logo.png" 
                alt="SPLICE" 
                className="logo-image"
              />
            </div>
            <button
              type="button"
              className="modern-close-btn"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* コンテンツ */}
          <div className="modern-auth-body">
            <div className="auth-title">
              <h2>パスワード変更</h2>
            </div>

            {loading ? (
              <div className="auth-loading">
                <div className="loading-spinner"></div>
                <span>パスワード変更中...</span>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="auth-form">
                {/* エラーメッセージ */}
                {errors.general && (
                  <div className="auth-error">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1.33333L14.6667 14H1.33333L8 1.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V8.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 11.3333H8.00667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {errors.general}
                  </div>
                )}

                {/* 現在のパスワード */}
                <div className="form-group">
                  <label htmlFor="current_password">現在のパスワード</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="3.75" y="7.5" width="10.5" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.75 7.5V5.25C6.75 4.45435 7.06607 3.69129 7.62868 3.12868C8.19129 2.56607 8.95435 2.25 9.75 2.25C10.5456 2.25 11.3087 2.56607 11.8713 3.12868C12.4339 3.69129 12.75 4.45435 12.75 5.25V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      id="current_password"
                      name="current_password"
                      value={formData.current_password}
                      onChange={handleInputChange}
                      placeholder="現在のパスワード"
                      className={errors.current_password ? 'error' : ''}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => togglePasswordVisibility('current')}
                      disabled={loading}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        {showPasswords.current ? (
                          <path d="M6.5 6.5C6.5 5.67157 7.17157 5 8 5C8.82843 5 9.5 5.67157 9.5 6.5M3 8C3 8 5 5 8 5C11 5 13 8 13 8C13 8 11 11 8 11C5 11 3 8 3 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        ) : (
                          <path d="M3.5 3.5L12.5 12.5M6.5 6.5C6.5 5.67157 7.17157 5 8 5C8.82843 5 9.5 5.67157 9.5 6.5M3 8C3 8 5 5 8 5C11 5 13 8 13 8C13 8 11 11 8 11C5 11 3 8 3 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        )}
                      </svg>
                    </button>
                  </div>
                  {errors.current_password && <span className="field-error">{errors.current_password}</span>}
                </div>

                {/* 新しいパスワード */}
                <div className="form-group">
                  <label htmlFor="new_password">新しいパスワード</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="3.75" y="7.5" width="10.5" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.75 7.5V5.25C6.75 4.45435 7.06607 3.69129 7.62868 3.12868C8.19129 2.56607 8.95435 2.25 9.75 2.25C10.5456 2.25 11.3087 2.56607 11.8713 3.12868C12.4339 3.69129 12.75 4.45435 12.75 5.25V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      id="new_password"
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleInputChange}
                      placeholder="8文字以上"
                      className={errors.new_password ? 'error' : ''}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => togglePasswordVisibility('new')}
                      disabled={loading}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        {showPasswords.new ? (
                          <path d="M6.5 6.5C6.5 5.67157 7.17157 5 8 5C8.82843 5 9.5 5.67157 9.5 6.5M3 8C3 8 5 5 8 5C11 5 13 8 13 8C13 8 11 11 8 11C5 11 3 8 3 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        ) : (
                          <path d="M3.5 3.5L12.5 12.5M6.5 6.5C6.5 5.67157 7.17157 5 8 5C8.82843 5 9.5 5.67157 9.5 6.5M3 8C3 8 5 5 8 5C11 5 13 8 13 8C13 8 11 11 8 11C5 11 3 8 3 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        )}
                      </svg>
                    </button>
                  </div>
                  {errors.new_password && <span className="field-error">{errors.new_password}</span>}
                </div>

                {/* 新しいパスワード確認 */}
                <div className="form-group">
                  <label htmlFor="new_password_confirm">新しいパスワード確認</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="3.75" y="7.5" width="10.5" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.75 7.5V5.25C6.75 4.45435 7.06607 3.69129 7.62868 3.12868C8.19129 2.56607 8.95435 2.25 9.75 2.25C10.5456 2.25 11.3087 2.56607 11.8713 3.12868C12.4339 3.69129 12.75 4.45435 12.75 5.25V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      id="new_password_confirm"
                      name="new_password_confirm"
                      value={formData.new_password_confirm}
                      onChange={handleInputChange}
                      placeholder="新しいパスワードを再入力"
                      className={errors.new_password_confirm ? 'error' : ''}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => togglePasswordVisibility('confirm')}
                      disabled={loading}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        {showPasswords.confirm ? (
                          <path d="M6.5 6.5C6.5 5.67157 7.17157 5 8 5C8.82843 5 9.5 5.67157 9.5 6.5M3 8C3 8 5 5 8 5C11 5 13 8 13 8C13 8 11 11 8 11C5 11 3 8 3 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        ) : (
                          <path d="M3.5 3.5L12.5 12.5M6.5 6.5C6.5 5.67157 7.17157 5 8 5C8.82843 5 9.5 5.67157 9.5 6.5M3 8C3 8 5 5 8 5C11 5 13 8 13 8C13 8 11 11 8 11C5 11 3 8 3 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        )}
                      </svg>
                    </button>
                  </div>
                  {errors.new_password_confirm && <span className="field-error">{errors.new_password_confirm}</span>}
                </div>

                {/* 変更ボタン */}
                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  <span>パスワード変更</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal; 